// Google Hotel-Price structured data (JSON-LD).
// Emits a schema.org `Hotel` with a `makesOffer` built by LOOPING over rooms —
// one Offer per room type — which Google reads to include our DIRECT rate in its
// hotel price-comparison / free booking links.
// Ref: https://developers.google.com/hotels/hotel-prices/structured-data/hotel-price-structured-data
//
// READ-ONLY: this module only READS transformed room data and writes a <script>
// into <head>. It never mutates room/rate objects, never touches pricing
// calculations, and is completely separate from the reservation flow.
//
// NOTE: structured data COMPLEMENTS a Google Hotel Ads / Free-Booking-Links
// price feed — the `identifier` must match the hotel ID used in that feed.

const SCRIPT_ID = 'hotel-price-jsonld';

/** Slugify a room name for the per-offer identifier (e.g. "Standard Room" -> "standard-room"). */
function slugify(s) {
  return String(s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Lowest-priced rate for ONE room → { price, currency, taxIncluded } or null.
 * Reads the room's rate plans (falls back to room base price). Never mutates input.
 */
function lowestRateForRoom(room) {
  if (!room || typeof room !== 'object') return null;
  let best = null;
  const plans = Array.isArray(room.ratePlans) ? room.ratePlans : [];
  for (const rp of plans) {
    const p = Number(rp && rp.price);
    if (p > 0 && (!best || p < best.price)) {
      best = {
        price: p,
        currency: (rp && (rp.currency || rp.CurrencyCode || rp.ratePlanCurrencyCode)) || room.currency || room.currencyCode,
        // Our direct rates are shown tax-inclusive; honour an explicit rate flag if present.
        taxIncluded: rp && (rp.taxIncluded != null ? rp.taxIncluded
                    : rp.isTaxIncluded != null ? rp.isTaxIncluded
                    : rp.IsInclusive != null ? rp.IsInclusive : null),
      };
    }
  }
  const base = Number(room.basePrice);
  if (base > 0 && (!best || base < best.price)) {
    best = { price: base, currency: room.currency || room.currencyCode, taxIncluded: null };
  }
  return best;
}

/**
 * Build the JSON-LD object from real hotel + rooms data.
 * `makesOffer` is generated by looping over `rooms` (one Offer per room type).
 * Returns null when the essentials (name, identifier) or ALL room prices are missing —
 * we never emit fake/partial price data (no-fake-data rule).
 */
export function buildHotelPriceJsonLd(input) {
  try {
    // Guard against null / non-object input (default params only cover undefined).
    const src = input && typeof input === 'object' ? input : {};
    const { name, identifier, address, country, checkinDate, checkoutDate, rooms } = src;

    if (!name || !identifier) return null;

    // --- makesOffer: loop over rooms, one Offer each (skip rooms without a real price) ---
    const offers = [];
    const roomList = Array.isArray(rooms) ? rooms : [];
    for (const room of roomList) {
     try { // per-room guard: one bad/malformed room must never break the whole JSON-LD
      const rate = lowestRateForRoom(room);
      if (!rate || !(Number(rate.price) > 0) || !rate.currency) continue; // no-fake-data: skip
      const price = Number(rate.price);
      const currency = String(rate.currency);
      const roomName = (room && (room.name || room.RoomName || room.roomName)) || '';
      const slug = slugify(roomName);

      const priceSpecification = {
        '@type': 'CompoundPriceSpecification',
        price,
        priceCurrency: currency,
        // Direct rates are displayed inclusive ("incl. taxes & fees"); use the rate flag when known.
        valueAddedTaxIncluded: rate.taxIncluded != null ? Boolean(rate.taxIncluded) : true,
        priceComponent: [
          {
            '@type': 'UnitPriceSpecification',
            name: 'Base Rate',
            price,
            priceCurrency: currency,
          },
        ],
      };
      // Client's JSON format keeps checkin/checkout INSIDE priceSpecification.
      if (checkinDate) priceSpecification.checkinTime = String(checkinDate);
      if (checkoutDate) priceSpecification.checkoutTime = String(checkoutDate);

      const offer = {
        '@type': ['Offer', 'LodgingReservation'],
        identifier: slug ? `${identifier}-${slug}` : String(identifier),
        priceSpecification,
      };
      // Google (Rich Results) reads checkinTime/checkoutTime on the OFFER
      // (LodgingReservation) — so also set them here, else it flags them "missing".
      if (checkinDate) offer.checkinTime = String(checkinDate);
      if (checkoutDate) offer.checkoutTime = String(checkoutDate);
      offers.push(offer);
     } catch (e) { /* skip this one room, keep building the rest */ }
    }

    if (!offers.length) return null; // no valid priced room → emit nothing

    const data = {
      '@context': 'https://schema.org',
      '@type': 'Hotel',
      name: String(name),
      identifier: String(identifier),
      // Single room → object (matches Google's single-offer shape); multiple → array.
      makesOffer: offers.length === 1 ? offers[0] : offers,
    };

    // Address is a single string in our API — put it in streetAddress; add country
    // separately when known. Omit the whole block if we have nothing.
    const postal = {};
    if (address) postal.streetAddress = String(address);
    if (country) postal.addressCountry = String(country);
    if (Object.keys(postal).length) {
      data.address = { '@type': 'PostalAddress', ...postal };
    }

    return data;
  } catch (e) {
    // Never let structured-data generation break rendering.
    return null;
  }
}

/** Create/update (or remove, when data is null) the JSON-LD <script> in <head>. */
export function injectHotelPriceJsonLd(data) {
  try {
    if (typeof document === 'undefined' || !document.head) return;
    let el = document.getElementById(SCRIPT_ID);
    if (!data) {
      if (el) el.remove();
      return;
    }
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = SCRIPT_ID;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
  } catch (e) {
    /* DOM/serialise failure must never break the app */
  }
}

/** Remove the JSON-LD <script> (used on unmount). */
export function removeHotelPriceJsonLd() {
  try {
    if (typeof document === 'undefined') return;
    const el = document.getElementById(SCRIPT_ID);
    if (el) el.remove();
  } catch (e) {
    /* no-op */
  }
}

/**
 * Build a stable signature of the rooms' prices/currencies so a React effect can
 * depend on it (the rooms array ref changes every render). READ-ONLY.
 */
export function roomsPriceSignature(rooms) {
  try {
    if (!Array.isArray(rooms)) return '';
    return rooms
      .map((r) => {
        const plans = Array.isArray(r && r.ratePlans) ? r.ratePlans : [];
        const p = plans.map((rp) => `${rp && rp.price}:${(rp && (rp.currency || rp.CurrencyCode)) || ''}`).join('|');
        return `${(r && (r.name || r.RoomName)) || ''}#${p}#${(r && r.basePrice) || ''}`;
      })
      .join(';');
  } catch (e) {
    return '';
  }
}
